import { useEffect, useRef } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Display } from '@growtherapy/sprout-ui';

import flowerShadowBackground from '../../assets/backgrounds/flower-shadow.jpg';
import { LanguageSwitcherWrapper } from '../../components/LanguageSwitcherWrapper';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { NavbarWrapper } from '../../components/NavbarWrapper';
import { PageContainer } from '../../components/PageContainer';
import { PageProviders } from '../../components/PageProviders';
import { Theme } from '../../state/theme';
import { JOINING_TRANSITION_DURATION_MS } from './constants';

/**
 * Interstitial shown after the client submits their final pre-session measure
 * (Submit & join), sitting between the soft-block flow and the waiting room. It
 * mirrors the get-started page's side-by-side layout (content left, image
 * right) but vertically centers the content and shows a loading state while the
 * timer runs down.
 */
export function SoftBlockJoiningTransition({
  onTransitionComplete,
  durationMs = JOINING_TRANSITION_DURATION_MS,
}: {
  onTransitionComplete: () => void;
  durationMs?: number;
}) {
  const t = useTranslate();

  // Read the latest callback from a ref so a changing callback identity doesn't
  // restart the timer.
  const onTransitionCompleteRef = useRef(onTransitionComplete);
  onTransitionCompleteRef.current = onTransitionComplete;

  useEffect(
    function moveToWaitingRoomAfterDelay() {
      const timeoutId = setTimeout(() => onTransitionCompleteRef.current(), durationMs);
      return () => clearTimeout(timeoutId);
    },
    [durationMs],
  );

  return (
    <PageProviders theme={Theme.LIGHT}>
      {/* Side-by-side from `md` up; narrower screens stack the image below. */}
      <PageContainer className='md:flex-row'>
        <div className='flex w-full flex-col md:w-1/2'>
          <NavbarWrapper languageSwitcherComponent={LanguageSwitcherWrapper} />

          <div className='flex flex-1 flex-col justify-center px-6 pt-12 pb-12 md:pt-0 md:pb-0 lg:px-12 motion-safe:animate-[appear_2s_cubic-bezier(0.16,1,0.3,1)]'>
            <div className='gap-component-75 flex flex-col items-start'>
              <LoadingSpinner aria-hidden className='text-xl text-neutral-600 rebrand:text-text-subtle' />

              <Display.Small className='text-neutral-800 rebrand:text-text-default'>
                {t('telehealth-core:preSessionMeasures.joiningSessionNow')}
              </Display.Small>

              <Body.Small className='text-neutral-700 rebrand:text-text-subtle'>
                {t('telehealth-core:preSessionMeasures.thanksForTheUpdate')}
              </Body.Small>
            </div>
          </div>
        </div>

        {/* Decorative image: right half from `md` up, bottom on narrower screens. */}
        <div className='min-h-0 flex-1 md:w-1/2 md:flex-none'>
          <img src={flowerShadowBackground} alt='' className='h-full w-full object-cover' />
        </div>
      </PageContainer>
    </PageProviders>
  );
}
